import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchWithAuth } from '../../utils/api';
import RowsPerPageSelector from '../common/RowsPerPageSelector';
import Pagination from '../common/Pagination';
import SortSelector from '../common/SortSelector';

function Sessions({ user, handleLogout }) {
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'login_time', direction: 'desc' });
  const [layoutMode, setLayoutMode] = useState(window.innerWidth < 768 ? 'cards' : 'table');

  const fetchSessions = useCallback(() => {
    fetchWithAuth('/sessions', {}, user, handleLogout)
      .then(async res => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then(data => {
        setSessions(data || []);
      })
      .catch(err => setError(err.message));
  }, [user, handleLogout]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Track the narrow breakpoint with matchMedia so we transition in
  // both directions — the previous resize listener only shrank
  // table→cards on narrow and never restored on widen.
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const apply = (matches) => setLayoutMode(matches ? 'cards' : 'table');
    apply(mql.matches);
    const listener = (e) => apply(e.matches);
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, sortConfig]);

  const sortedSessions = useMemo(() => {
    let sortableItems = [...sessions];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle special cases
        if (sortConfig.key === 'user') {
          aVal = (a.user?.username || '').toLowerCase();
          bVal = (b.user?.username || '').toLowerCase();
        } else if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [sessions, sortConfig]);

  const paginatedSessions = useMemo(() => {
    if (rowsPerPage === 'all') return sortedSessions;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedSessions.slice(start, start + rowsPerPage);
  }, [sortedSessions, currentPage, rowsPerPage]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div className="view-container">
      <div className="view-header">
        <h2>Active Sessions</h2>
        <div className="view-actions">
          <button className="action-btn" onClick={fetchSessions}>Refresh</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      <div className="list-controls">
        <div className="control-group">
          <RowsPerPageSelector value={rowsPerPage} onChange={setRowsPerPage} />
          {layoutMode === 'cards' && (
            <SortSelector 
              options={[
                { key: 'login_time', label: 'Login Time' },
                { key: 'user', label: 'User' },
                { key: 'location', label: 'Location' }
              ]} 
              sortConfig={sortConfig} 
              onSortChange={setSortConfig} 
            />
          )}
        </div>
        <div className="layout-toggle no-print">
          <button className={layoutMode === 'table' ? 'active' : ''} onClick={() => setLayoutMode('table')}>Table</button>
          <button className={layoutMode === 'cards' ? 'active' : ''} onClick={() => setLayoutMode('cards')}>Cards</button>
        </div>
      </div>

      {layoutMode === 'table' ? (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th onClick={() => requestSort('user')} className="sortable">User {sortConfig.key === 'user' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('login_time')} className="sortable">Login Time {sortConfig.key === 'login_time' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th>IP Address</th>
                <th>Location</th>
                <th>Browser</th>
                <th>Device</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSessions.map((s) => (
                <tr key={`${s.user.username}-${s.login_time}-${s.ip_address}`}>
                  <td><strong>{s.user.username}</strong> <span style={{fontSize: '0.8rem', color: 'var(--text-secondary)'}}>({s.user.role})</span></td>
                  <td>{new Date(s.login_time).toLocaleString()}</td>
                  <td>{s.ip_address}</td>
                  <td>{s.location || 'Unknown'}</td>
                  <td>{s.browser}</td>
                  <td>{s.device_type}</td>
                </tr>
              ))}
              {paginatedSessions.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center" style={{padding: '2rem', color: 'var(--text-secondary)'}}>No active sessions found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-grid">
          {paginatedSessions.map((s) => (
            <div key={`card-${s.user.username}-${s.login_time}-${s.ip_address}`} className="data-card">
              <div className="card-header">
                <div className="card-title-group">
                  <h4>{s.user.username}</h4>
                  <span style={{fontSize: '0.75rem', color: 'var(--text-secondary)', background: '#f1f3f4', padding: '0.1rem 0.4rem', borderRadius: '4px'}}>{s.user.role}</span>
                </div>
                <span className="card-date">{new Date(s.login_time).toLocaleDateString()}</span>
              </div>
              <div className="card-body">
                <div className="card-item"><span className="label">Login Time</span><span className="value">{new Date(s.login_time).toLocaleTimeString()}</span></div>
                <div className="card-item"><span className="label">IP Address</span><span className="value">{s.ip_address}</span></div>
                <div className="card-item"><span className="label">Location</span><span className="value highlight">{s.location || 'Unknown'}</span></div>
                <div className="card-item"><span className="label">Browser</span><span className="value">{s.browser}</span></div>
                <div className="card-item"><span className="label">Device</span><span className="value">{s.device_type}</span></div>
              </div>
            </div>
          ))}
          {paginatedSessions.length === 0 && (
            <div className="text-center" style={{gridColumn: '1/-1', padding: '3rem', color: 'var(--text-secondary)'}}>No active sessions found.</div>
          )}
        </div>
      )}

      <Pagination 
        totalItems={sessions.length} 
        itemsPerPage={rowsPerPage} 
        currentPage={currentPage} 
        onPageChange={setCurrentPage} 
      />
    </div>
  );
}

export default Sessions;
